import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core'

// Currency-aware. It used to hardcode "TL", which on a USD document would have
// labelled every figure with the wrong unit - including on the screen where an
// operator decides whether to commit to it.
const money = (v, currency = 'TRY') =>
  `${Number(v).toFixed(2).replace('.', ',')} ${currency === 'TRY' ? 'TL' : currency}`
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '-')

/**
 * The calendar day in Türkiye, as yyyy-MM-dd.
 *
 * NOT toISOString().slice(0, 10), which is the UTC day. Türkiye is UTC+3 year
 * round, so between 00:00 and 03:00 Istanbul that is YESTERDAY — and on 1
 * January it is the previous YEAR. The value seeds the date field, bounds it,
 * and is transmitted as an override the server accepts (a past date at or after
 * the sale is legitimate back-dating), so the document would land in the
 * previous VAT period against a serial sequence that restarts on 1 January.
 *
 * en-CA is the locale whose short date format is already yyyy-MM-dd.
 */
const trDay = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(d)
const isoDay = (iso) => (iso ? trDay(new Date(iso)) : '')

/**
 * Confirm and file one document.
 *
 * Shows what is about to be declared before asking for the decision, because
 * the operator's job here is to notice something wrong, and they cannot notice
 * what the screen does not show.
 */
export default function KesDialog({ row, open, onClose, onConfirm, busy, error, onPreview, previewing, previewError }) {
  const today = trDay(new Date())
  const [issueDate, setIssueDate] = useState(today)
  // The sale date printed on the document as "Sipariş Tarihi". Separate from
  // the invoice date above, and that separation is the point: a document raised
  // today for an older sale states both, instead of back-dating itself into a
  // VAT period that has already been declared.
  const [orderDate, setOrderDate] = useState('')

  React.useEffect(() => {
    if (open) {
      setIssueDate(today)
      setOrderDate(isoDay(row?.order_at) || isoDay(row?.sale_at))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.id])

  if (!row) return null

  // order_at first, for the same reason the queue column prefers it: on an
  // admitted row it is the only sale date there is, and using sale_at left the
  // invoice-date picker with no lower bound at all.
  const saleDay = isoDay(row.order_at) || isoDay(row.sale_at)
  // Late by the 7-day rule, so the two dates will visibly differ. Worth saying
  // once, plainly, rather than leaving the operator to subtract the dates.
  const lateDays = orderDate && issueDate ? Math.round((new Date(issueDate) - new Date(orderDate)) / 86400000) : 0

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Fatura Kes</DialogTitle>
      <DialogContent dividers>
        {/*
          The one thing that must be read before anything else. A row carrying
          create_attempted_at may already have produced a document at Mysoft
          that we never heard about, and filing it again by hand is the single
          way this design yields two legally binding invoices for one sale.
        */}
        {row.create_attempted_at && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <b>Bu satır için daha önce kesim isteği gönderilmiş ve sonucu alınamamış.</b>
            <br />
            Devam etmeden önce Mysoft portalında <code>{row.payment_id}</code> referans numarasını arayın. Belge zaten
            oluşmuş olabilir.
          </Alert>
        )}

        <Stack spacing={1.25} sx={{ mb: 2 }}>
          <Row label="Müşteri" value={row.organisation} />
          <Row label="Alıcı ünvanı" value={row.buyer_title || '-'} />
          <Row
            label="VKN"
            value={
              <>
                {row.buyer_vkn}
                {row.registry_registered && ' · GİB e-Fatura mükellefi'}
              </>
            }
          />
          <Row label="Belge" value={`${row.doc_type || '-'}${row.profile ? ` / ${row.profile}` : ''}`} />
          <Divider />

          {/*
            The LINES, not just a total.

            A Genesus sale is two lines at two different KDV rates: the
            teknokent-exempt part and the taxed remainder. An operator shown one
            total cannot tell whether the istisna was applied at all, and
            approving what you cannot see is not approval.
          */}
          {(row.lines || []).length > 0 && (
            <Table size="small" sx={{ '& td, & th': { px: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Mal / Hizmet</TableCell>
                  <TableCell align="right">Matrah</TableCell>
                  <TableCell align="right">KDV</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.lines.map((line, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell>
                      {line.description}
                      {line.exemption_code && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          İstisna {line.exemption_code} · {line.exemption_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{money(line.matrah, row.currency)}</TableCell>
                    <TableCell align="right">
                      %{Number(line.vat_rate)} · {money(line.kdv, row.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Divider />
          <Row label="Mal hizmet toplamı" value={money(row.amount_subtotal, row.currency)} />
          <Row label="Hesaplanan KDV" value={money(row.amount_tax, row.currency)} />
          {Number(row.rounding_delta) !== 0 && (
            <Row label="Yuvarlama" value={money(row.rounding_delta, row.currency)} />
          )}
          {Number(row.amount_finance) > 0 && (
            <Row label="Vade farkı (dahil)" value={money(row.amount_finance, row.currency)} />
          )}
          <Row label="Ödenecek" value={<b>{money(row.amount_total, row.currency)}</b>} />

          {/*
            A foreign-currency document is legally binding in USD, but the money
            that moved was TRY. Showing the rate and the TL equivalent lets an
            operator check the document against the bank before committing.
          */}
          {row.currency && row.currency !== 'TRY' && (
            <Row
              label="TL karşılığı"
              value={
                <>
                  {money(row.amount_total_try, 'TRY')}
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    kur {Number(row.currency_rate).toFixed(6).replace('.', ',')} · tahsil edilen tutar
                  </Typography>
                </>
              }
            />
          )}
        </Stack>

        <TextField
          label="Fatura tarihi"
          type="date"
          fullWidth
          size="small"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
          // Bounded to the same window the server enforces. The picker only
          // saves a pointless round trip; it is not the control.
          inputProps={{ min: saleDay || undefined, max: today }}
          InputLabelProps={{ shrink: true }}
          helperText={`Satış ${day(row.order_at || row.sale_at)} · bugün ${day(
            new Date().toISOString(),
          )}. Satıştan önce veya ileri tarihli olamaz.`}
        />

        <TextField
          label="Sipariş tarihi (satış)"
          type="date"
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          value={orderDate}
          onChange={(e) => setOrderDate(e.target.value)}
          // No lower bound on purpose: the field exists to carry a sale that is
          // months behind the filing date, so a floor would forbid exactly the
          // case it is for. The upper bound is real - an order cannot postdate
          // its own invoice - and the server enforces the same two.
          inputProps={{ max: issueDate || today }}
          InputLabelProps={{ shrink: true }}
          helperText={
            row.sale_at
              ? 'Belgede "Sipariş Tarihi" olarak görünür. Faturanın kendi tarihi yukarıdaki alandır.'
              : 'Bu satışın tarihi sistemde kayıtlı değil; belgede görünecek tarihi siz giriyorsunuz.'
          }
        />

        {lateDays > 7 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Satışın üzerinden {lateDays} gün geçmiş. Belge bugünün tarihiyle kesilecek, sipariş tarihi {day(orderDate)}{' '}
            olarak yazılacak. Fatura geriye dönük tarihlenmiyor.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {previewError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {previewError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/*
          Reading the document beats reading a form describing it, and this
          costs nothing: Mysoft renders it without filing, no serial number is
          consumed and the row is not touched. It uses the date chosen above, so
          what is on screen is what would be sent.
        */}
        <Button onClick={() => onPreview(issueDate, orderDate)} disabled={busy || previewing}>
          {previewing ? 'Hazırlanıyor…' : 'Önizle (PDF)'}
        </Button>
        <Box>
          <Button onClick={onClose} disabled={busy} sx={{ mr: 1 }}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={() => onConfirm(issueDate, orderDate)} disabled={busy}>
            {busy ? 'Kesiliyor…' : 'Kes'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 140 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

Row.propTypes = { label: PropTypes.string, value: PropTypes.node }

KesDialog.propTypes = {
  row: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  busy: PropTypes.bool,
  error: PropTypes.string,
  onPreview: PropTypes.func,
  previewing: PropTypes.bool,
  previewError: PropTypes.string,
}
